import { Navigate, useLocation } from "react-router";
import type { ReactNode } from "react";
import { useAuth } from "../auth/useAuth";
import { canEnterRoute } from "../auth/authorities";
import { byPath } from "./navigation";

/**
 * No session means back to /login, carrying the attempted URL in `?url=` so
 * the login page can return the user to it.
 */
export function RequireSession({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  if (!isAuthenticated) {
    const url = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?url=${encodeURIComponent(url)}`} replace />;
  }
  return <>{children}</>;
}

/**
 * Gates a route on its authority set, read from the NAV table rather than
 * repeated at each route definition.
 */
export function RequireAuthority({ path, children }: { path: string; children: ReactNode }) {
  const { principal } = useAuth();
  const entry = byPath.get(path);
  const authorities = entry?.authorities ?? [];
  // An ungated route is reachable by anyone with a session.
  if (authorities.length === 0) return <>{children}</>;
  if (!canEnterRoute(principal, authorities)) {
    return <Navigate to="/dashboard/unauthorized" replace />;
  }
  return <>{children}</>;
}
